import React from 'react';
import styled, { css } from 'styled-components';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

export interface MenuItemConfig {
  icon: React.ReactElement;
  title: string;
  path: string;
  subMenu?: MenuItemConfig[] | null;
}

interface CustomSidebarProps {
  isCollapsed: boolean;
  toggleSidebar: () => void;
  menuItems: MenuItemConfig[];
  footerIcon: React.ReactElement;
  onFooterClick: () => void;
  children: React.ReactNode;
}

const CustomSidebar: React.FC<CustomSidebarProps> = ({
  isCollapsed,
  toggleSidebar,
  menuItems,
  footerIcon,
  onFooterClick,
  children,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const [expandedMenu, setExpandedMenu] = React.useState<string | null>(null);
  const [overlayMenu, setOverlayMenu] = React.useState<string | null>(null);
  const [overlayMenuRect, setOverlayMenuRect] = React.useState<DOMRect | null>(null);

  const isActive = (itemPath: string) => pathname === itemPath;

  const isMenuExpanded = (subMenu?: MenuItemConfig[] | null) => {
    if (!subMenu) return false;
    return subMenu.some((sub) => pathname.startsWith(sub.path));
  };

  const handleMenuClick = (path: string, hasSubMenu: boolean, subMenu?: MenuItemConfig[] | null, event?: React.MouseEvent<HTMLDivElement>) => {
    if (hasSubMenu && subMenu) {
      if (isCollapsed) {
        // 사이드바가 닫혔을 때: 오버레이 서브메뉴 토글
        const isCurrentlyOpen = overlayMenu === path;
        
        if (isCurrentlyOpen) {
          setOverlayMenu(null);
          setOverlayMenuRect(null);
        } else {
          // 다른 오버레이 메뉴가 열려있다면 닫고 새로운 메뉴 열기
          setOverlayMenu(path);
          if (event) {
            const rect = event.currentTarget.getBoundingClientRect();
            setOverlayMenuRect(rect);
          }
          // 서브메뉴의 첫 번째 항목으로 이동
          if (subMenu.length > 0) {
            navigate(subMenu[0].path);
          }
        }
      } else {
        // 사이드바가 열렸을 때: 기존 로직 유지
        const isCurrentlyExpanded = expandedMenu === path;
        
        if (isCurrentlyExpanded) {
          setExpandedMenu(null);
        } else {
          setExpandedMenu(path);
          if (subMenu.length > 0) {
            navigate(subMenu[0].path);
          }
        }
      }
    } else {
      // 서브메뉴가 없는 경우 페이지 이동하고 모든 확장된 메뉴 축소
      setExpandedMenu(null);
      setOverlayMenu(null);
      setOverlayMenuRect(null);
      navigate(path);
    }
  };

  // 오버레이 메뉴 외부 클릭시 닫기
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (overlayMenu && overlayMenuRect) {
        const target = event.target as HTMLElement;
        const sidebarElement = document.querySelector('[data-sidebar="true"]');
        const overlayElement = document.querySelector('[data-overlay-menu="true"]');
        
        if (!sidebarElement?.contains(target) && !overlayElement?.contains(target)) {
          setOverlayMenu(null);
          setOverlayMenuRect(null);
        }
      }
    };

    if (overlayMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [overlayMenu, overlayMenuRect]);

  return (
    <SidebarContainer $isCollapsed={isCollapsed} data-sidebar="true">
      {children}

      <ToggleButton
        onClick={toggleSidebar}
        $isCollapsed={isCollapsed}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ToggleIconImg
          src="/icon_burger.png"
          alt={isCollapsed ? 'Expand' : 'Collapse'}
          width={16}
          height={24}
          $rotate={isCollapsed}
        />
      </ToggleButton>

      <MenuList $isCollapsed={isCollapsed}>
        {menuItems.map((item) => {
          const hasSubMenu = !!item.subMenu;
          const active = isActive(item.path);
          const expanded = expandedMenu === item.path;
          // 접힌 상태에서는 현재 경로가 서브메뉴에 포함되는지 확인
          const shouldBeActive = isCollapsed 
            ? (active || isMenuExpanded(item.subMenu))
            : (active || isMenuExpanded(item.subMenu));

          return (
            <React.Fragment key={item.path}>
              {hasSubMenu ? (
                <MenuItem
                  as="div"
                  $isCollapsed={isCollapsed}
                  $active={shouldBeActive}
                  onClick={(e) => handleMenuClick(item.path, true, item.subMenu, e)}
                >
                  <Center $isCollapsed={isCollapsed}>
                    <IconWrapper $isCollapsed={isCollapsed}>
                      {item.icon}
                    </IconWrapper>

                    {!isCollapsed && (
                      <RightContent>
                        <span>{item.title}</span>
                        {hasSubMenu && (
                          <ArrowWrapper>
                            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                          </ArrowWrapper>
                        )}
                      </RightContent>
                    )}

                  </Center>


                </MenuItem>
              ) : (
                <MenuItem 
                  as="div" 
                  $isCollapsed={isCollapsed} 
                  $active={active}
                  onClick={() => handleMenuClick(item.path, false)}
                >
                  <Center $isCollapsed={isCollapsed}>
                    <IconWrapper $isCollapsed={isCollapsed}>
                      {item.icon}
                    </IconWrapper>
                    {!isCollapsed && (
                      <RightContent>
                        <span>{item.title}</span>
                        {/* 서브메뉴 없으면 ArrowWrapper 렌더 안함 */}
                      </RightContent>
                    )}
                  </Center>
                </MenuItem>

              )}

              {expanded && item.subMenu && !isCollapsed && (
                <SubMenuList>
                  {item.subMenu.map((subItem) => (
                    <SubMenuItem 
                      key={subItem.path}
                      $active={pathname === subItem.path}
                      onClick={() => navigate(subItem.path)}
                    >
                      {subItem.title}
                    </SubMenuItem>
                  ))}
                </SubMenuList>
              )}
            </React.Fragment>
          );
        })}
      </MenuList>

      <FooterSection onClick={onFooterClick} $isCollapsed={isCollapsed}>
        <IconWrapper $isCollapsed={isCollapsed}>{footerIcon}</IconWrapper>
        {!isCollapsed && <FooterText>로그아웃</FooterText>}
      </FooterSection>

      {/* 오버레이 서브메뉴 (사이드바가 닫혔을 때만) */}
      {isCollapsed && overlayMenu && overlayMenuRect && (
        <OverlaySubMenu
          $top={overlayMenuRect.top}
          $left={overlayMenuRect.right +3}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          data-overlay-menu="true"
        >
          {menuItems
            .find((item) => item.path === overlayMenu)
            ?.subMenu?.map((subItem) => (
              <OverlaySubMenuItem
                key={subItem.path}
                $active={pathname === subItem.path}
                onClick={() => {
                  navigate(subItem.path);
                  setOverlayMenu(null);
                  setOverlayMenuRect(null);
                }}
              >
                {subItem.title}
              </OverlaySubMenuItem>
            ))}
        </OverlaySubMenu>
      )}
    </SidebarContainer>
  );
};

export default CustomSidebar;

// --- Styled Components ---

const SidebarContainer = styled.div<{ $isCollapsed: boolean }>`
  position: fixed;
  left: 0;
  top: 0;
  width: ${({ $isCollapsed }) => ($isCollapsed ? '80px' : '250px')};
  height: 100vh;
  overflow: hidden;
  background: #2c2e3c;
  color: white;
  display: flex;
  flex-direction: column;
  transition: width 0.3s ease;
  z-index: 1000;
`;

const ToggleButton = styled.button<{ $isCollapsed: boolean }>`
  position: absolute;
  top: 50px;
  right: 15px;
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`;

const RightContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  gap: 8px;
  padding-right: 30px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;


const ToggleIconImg = styled.img<{ $rotate?: boolean }>`
  display: block;
  transform: ${({ $rotate }) => ($rotate ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.3s ease;
`;

const MenuList = styled.ul<{ $isCollapsed: boolean }>`
  list-style: none;
  padding: 0;
  margin: 0;
  padding-top: 60px;
  overflow-y: auto;
  overflow-x: hidden;
  flex-grow: 1;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  ${({ $isCollapsed }) =>
    $isCollapsed &&
    css`
      display: flex;
      flex-direction: column;
      align-items: center;
    `}
`;

const MenuItem = styled.li<{ $active?: boolean; $isCollapsed: boolean }>`
  height: 60px;
  display: flex;
  justify-content: ${({ $isCollapsed }) =>
    $isCollapsed ? 'center' : 'flex-start'};
  align-items: center;
  color: ${({ $active }) => ($active ? '#fff' : '#8d8e96')};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  /* border-bottom: 1px solid #252736; */
  transition: background 0.2s, color 0.2s;
  background-color: ${({ $active }) => ($active ? '#3a3f4e' : '#2c2e3c')};
  padding-left: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '20px')};
  
  border-radius: 5px;
  width: 100%;
  text-decoration: none;
  position: relative;

  &:hover {
    background-color: #3a3f4e;
    color: white;
  }
`;

const Center = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  width: 100%;
  overflow: hidden;
  ${({ $isCollapsed }) =>
    $isCollapsed
      ? css`
          justify-content: center;
        `
      : css`
          justify-content: flex-start;
        `}
`;

const ArrowWrapper = styled.span`
  margin-left: 8px;
  display: flex;
  align-items: center;
`;

const IconWrapper = styled.span<{ $isCollapsed?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '15px')};
  flex-shrink: 0;

  & > svg {
    width: 100%;
    height: 100%;
  }
`;

const FooterSection = styled.div<{ $isCollapsed: boolean }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $isCollapsed }) =>
    $isCollapsed ? 'center' : 'flex-start'};
  padding: 15px;
  height: 60px;
  color: #8d8e96;
  cursor: pointer;
  transition: color 0.2s, background-color 0.2s;
  border-top: 1px solid #444;
  flex-shrink: 0;

  &:hover {
    color: white;
    background-color: #3a3f4e;
  }
`;

const FooterText = styled.span`
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SubMenuList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const SubMenuItem = styled.li<{ $active?: boolean }>`
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: flex-end; /* 오른쪽 정렬 */
  color: ${({ $active }) => ($active ? '#fff' : '#797878')};
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  padding-right: 20px; /* 오른쪽 여백 */
  background-color: ${({ $active }) => ($active ? '#3a3f4e' : 'transparent')};

  &:hover {
    background-color: #3a3f4e;
    color: white;
  }
`;

const OverlaySubMenu = styled.div<{ $top: number; $left: number }>`
  position: fixed;
  top: ${({ $top }) => $top}px;
  left: ${({ $left }) => $left}px;
  background: rgba(44, 46, 60, 0.8);
  border: 1px solid #444;
  border-radius: 5px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  backdrop-filter: blur(4px);
  z-index: 1100;
  min-width: 180px;
  overflow: hidden;
`;

const OverlaySubMenuItem = styled.div<{ $active?: boolean }>`
  height: 40px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  color: ${({ $active }) => ($active ? '#fff' : '#797878')};
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  background-color: ${({ $active }) => ($active ? 'rgba(58, 63, 78, 0.5)' : 'transparent')};

  &:hover {
    background-color: rgba(58, 63, 78, 0.8);
    color: white;
  }
`;
